import { useState } from 'react';
import React from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';

interface TokenUsageRecord {
  record_id?: string;
  timestamp?: string;
  timestamp_request?: string;
  timestamp_response?: string;
  user_id?: string;
  client_id?: string;
  project_id?: string;
  sub_project_id?: string;
  model?: string;
  model_id?: string;
  prompt_name?: string;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  tokens_input_cached?: number;
  tokens_input_uncached?: number;
  tokens_output?: number;
  estimated_cost_cents?: number;
  estimated_cost_usd?: number;
  execution_mode?: string;
}

interface LLMModel {
  model_id: string;
  model_host: string;
  model_name: string;
  billing_type: string;
  pricing: {
    input_cached?: number;
    input_uncached?: number;
    output?: number;
    per_1000_pages?: number;
  };
  batch_reduction: number;
  context_size: number;
  effective_date: string;
}

export default function SettingsAdmin() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [records, setRecords] = useState<TokenUsageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordCount, setRecordCount] = useState<number>(0);

  const [models, setModels] = useState<LLMModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const canReseed = user && ['SysAdmin', 'DevOps'].includes(user.role || '');

  const handleFetchRecords = async () => {
    if (!selectedDate) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/token-usage/daily?date=${selectedDate}`);
      const data = response.data;

      setRecords(data.records || []);
      setRecordCount(data.record_count || 0);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      console.error('Error fetching token usage:', err);
      setError(e.response?.data?.detail || 'Failed to fetch token usage records');
      setRecords([]);
      setRecordCount(0);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const formatCost = (record: TokenUsageRecord) => {
    if (record.estimated_cost_cents !== undefined) {
      return `$${(record.estimated_cost_cents / 100).toFixed(4)}`;
    }
    if (record.estimated_cost_usd !== undefined) {
      return `$${record.estimated_cost_usd.toFixed(4)}`;
    }
    return 'N/A';
  };

  const getTotalTokens = (record: TokenUsageRecord) => {
    if (record.total_tokens !== undefined) {
      return record.total_tokens;
    }
    if (record.tokens_input_cached !== undefined || record.tokens_input_uncached !== undefined || record.tokens_output !== undefined) {
      const cached = record.tokens_input_cached || 0;
      const uncached = record.tokens_input_uncached || 0;
      const output = record.tokens_output || 0;
      return cached + uncached + output;
    }
    const input = record.input_tokens || 0;
    const output = record.output_tokens || 0;
    return input + output;
  };

  const getInputTokensDisplay = (record: TokenUsageRecord) => {
    if (record.tokens_input_cached !== undefined || record.tokens_input_uncached !== undefined) {
      const cached = record.tokens_input_cached || 0;
      const uncached = record.tokens_input_uncached || 0;
      if (cached > 0) {
        return `${cached + uncached} (${cached} cached)`;
      }
      return `${uncached}`;
    }
    return `${record.input_tokens || 0}`;
  };

  const handleFetchModels = async () => {
    setModelsLoading(true);
    setModelsError(null);

    try {
      const response = await api.get('/token-usage/models');
      setModels(response.data.models || []);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      console.error('Error fetching LLM models:', err);
      setModelsError(e.response?.data?.detail || 'Failed to fetch LLM models');
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleReseedModels = async () => {
    if (!window.confirm('Are you sure you want to update the LLM registry from the seed file? This will add any new models or pricing versions found in the CSV.')) {
      return;
    }

    setModelsLoading(true);
    setModelsError(null);

    try {
      const response = await api.post('/token-usage/reseed');
      const details = response.data.details;
      alert(`Sync successful!\n\nNew Models Added: ${details.success}\nPricing Updated: ${details.updated}\nSkipped (Up to date): ${details.skipped}\nErrors: ${details.errors}`);
      // Refresh the list automatically
      await handleFetchModels();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      console.error('Error reseeding LLM models:', err);
      const errorMsg = e.response?.data?.detail || 'Failed to sync LLM models';
      setModelsError(errorMsg);
    } finally {
      setModelsLoading(false);
    }
  };

  const formatPricing = (model: LLMModel) => {
    if (model.billing_type === 'per_token') {
      const cached = model.pricing.input_cached || 0;
      const uncached = model.pricing.input_uncached || 0;
      const output = model.pricing.output || 0;
      return (
        <span className="text-xs leading-5">
          In(c):{cached} In(u):{uncached} Out:{output}
        </span>
      );
    }
    return `${model.pricing.per_1000_pages} ¢/1K pages`;
  };

  const TH = ({ children }: { children: React.ReactNode }) => (
    <th className="text-left py-1.5 pr-3 text-xs font-medium text-slate-400 border-b border-slate-100">{children}</th>
  );
  const TD = ({ children, mono }: { children: React.ReactNode; mono?: boolean }) => (
    <td className={`py-1.5 pr-3 text-xs text-slate-600 border-b border-slate-50 ${mono ? 'font-mono' : ''}`}>{children}</td>
  );

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader title="Token Usage Admin" />

      {/* ── Token Usage by Date ─────────────────────────────────── */}
      <div className="px-6 pt-4">
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Token Usage</h3>
          <input
            id="date-input" type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400"
          />
          <Button size="sm" variant="outline" onClick={handleFetchRecords} disabled={loading || !selectedDate}>
            {loading ? 'Fetching...' : 'Fetch Records'}
          </Button>
          {recordCount > 0 && (
            <span className="text-xs text-slate-500">{recordCount} record{recordCount !== 1 ? 's' : ''} for <strong>{selectedDate}</strong></span>
          )}
        </div>

        {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

        {records.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <TH>Timestamp</TH><TH>User ID</TH><TH>Project ID</TH>
                <TH>Model</TH><TH>Prompt</TH><TH>Input Tokens</TH>
                <TH>Output</TH><TH>Total</TH><TH>Cost</TH>
              </tr></thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={record.record_id || index} className="hover:bg-slate-50/50">
                    <TD mono>{formatTimestamp(record.timestamp_request || record.timestamp)}</TD>
                    <TD mono><span title={record.user_id}>{record.user_id?.substring(0, 8) || 'N/A'}…</span></TD>
                    <TD mono><span title={record.project_id || record.sub_project_id}>{(record.project_id || record.sub_project_id)?.substring(0, 8) || 'N/A'}…</span></TD>
                    <TD mono>{record.model_id || record.model || 'N/A'}</TD>
                    <TD>{record.prompt_name || 'N/A'}</TD>
                    <TD>{getInputTokensDisplay(record)}</TD>
                    <TD>{record.tokens_output || record.output_tokens || 0}</TD>
                    <TD><strong>{getTotalTokens(record)}</strong></TD>
                    <TD>{formatCost(record)}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !loading && selectedDate ? (
          <p className="text-sm text-slate-400 py-1">No token usage records for {selectedDate}.</p>
        ) : null}

        <hr className="border-slate-100 my-4" />

        {/* ── LLM Models Pricing ──────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-semibold text-slate-700">LLM Models Pricing</h3>
          <Button size="sm" variant="outline" onClick={handleFetchModels} disabled={modelsLoading}>
            {modelsLoading ? 'Loading...' : 'Fetch Models'}
          </Button>
          {canReseed && (
            <Button size="sm" variant="ghost" onClick={handleReseedModels} disabled={modelsLoading}
              title="Sync database with config/llm_models_seed.csv">
              {modelsLoading ? 'Syncing...' : 'Sync from Seed'}
            </Button>
          )}
          {models.length > 0 && (
            <span className="text-xs text-slate-500">{models.length} active model{models.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {modelsError && <p className="text-xs text-red-600 mb-2">{modelsError}</p>}

        {models.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <TH>Model ID</TH><TH>Host</TH><TH>Name</TH>
                <TH>Billing</TH><TH>Pricing (¢)</TH><TH>Batch</TH>
                <TH>Context</TH><TH>Effective</TH>
              </tr></thead>
              <tbody>
                {models.map((model, index) => (
                  <tr key={`${model.model_id}-${model.model_host}-${index}`} className="hover:bg-slate-50/50">
                    <TD mono>{model.model_id}</TD>
                    <TD>{model.model_host}</TD>
                    <TD>{model.model_name}</TD>
                    <TD>{model.billing_type}</TD>
                    <td className="py-1.5 pr-3 text-xs text-slate-600 border-b border-slate-50">{formatPricing(model)}</td>
                    <TD>{model.batch_reduction}%</TD>
                    <TD>{model.context_size.toLocaleString()}</TD>
                    <TD>{model.effective_date}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !modelsLoading ? (
          <p className="text-sm text-slate-400 py-1">No models loaded. Click Fetch Models.</p>
        ) : null}
      </div>
    </div>
  );
}
